///
/// EarthBrowser
///
/// Created by Matt Giger on 10/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// City weather vignette: the live-conditions diorama shown over a focused
/// city (planning/LIVE_DATA_DELIGHT_FEATURES.md). A translucent plinth on
/// the ground, a ray-marched cloud above it whose cover, flatness and
/// darkness follow the sampled conditions, rain or snow falling between
/// them, and lightning flicker in thunderstorms. Everything is sized from
/// the city's framing radius so a town and a metro read the same at their
/// respective focus distances.
///
/// Conditions come from the current-weather grid at the scene clock (same
/// source and cadence as the city label chips), so scrubbing time changes
/// the scene. The sun direction that shades the globe lights the cloud.
///
/// Lifecycle: the owning CityLabelNode adds this as a child on focus
/// gained and calls show(); hide() fades out and reports completion so the
/// owner can drop the node. The vignette also dissolves as the camera
/// closes in past plinth scale, so it never covers the imagery the user
/// zoomed in to see.
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { type CurrentWeather } from "../../weather-grid/WeatherCompute"
import { getWeatherGridService } from "../../weather-grid/WeatherGridService"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { requestRender } from "@stovokor/engine/RenderRequest"
import { RenderState } from "@stovokor/engine/RenderState"
import { animation, easing } from "@stovokor/math/animation"
import { rgba } from "@stovokor/math/color"
import { clamp, trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { dot, normal, vec2, vec3 } from "@stovokor/math/vector"
import { VignetteCloudNode } from "./VignetteCloudNode"
import { VignettePlinthNode } from "./VignettePlinthNode"
import { VignettePrecipNode } from "./VignettePrecipNode"

// Scene-clock movement that triggers a re-sample, and the real-time
// refresh for a parked clock (matches LocationsDatasetLayer's label passes)
const cClockResampleMs = 15 * 60_000
const cRealtimeResampleMs = 5 * 60_000

// Fade durations (seconds)
const cShowDuration = 0.7
const cHideDuration = 0.4

// Camera distance to the city in framing radii: fully gone by the near end
// so zooming into the city clears the diorama; fading again toward the
// far end before GroundFocusAdapter's auto-dismiss (5 framing distances)
const cNearFadeStart = 1.3
const cNearFadeEnd = 0.8
const cFarFadeStartDistances = 3.0
const cFarFadeEndDistances = 4.5

// Thunderstorm flashes: interval range and decay
const cFlashMinGapS = 1.5
const cFlashMaxGapS = 6
const cFlashDecayS = 0.35

export interface VignetteSnapshot {
	visible: boolean
	opacity: number
	framingRadiusKm: number
	weather: CurrentWeather | null
	sunElevation: number
	flash: number
}

export class CityWeatherVignetteNode extends NodeBase {
	private readonly context: EarthBrowserContext
	private readonly geo: geographic
	private readonly framingRadiusKm: number
	private readonly framingDistanceKm: number

	private readonly plinth: VignettePlinthNode
	private readonly cloud: VignetteCloudNode
	private readonly precip: VignettePrecipNode

	private weather: CurrentWeather | null = null
	// Dev override merged over the live sample (window.__ebDebug.setCityVignetteWeather)
	private weatherOverride: Partial<CurrentWeather> | null = null
	private sampleInFlight = false
	private sampledAtMs = 0
	private sampledClockMs = 0

	private fade = 0
	private fadeAnim?: animation
	private distanceFade = 1
	private sunElevation = 1
	private startJulian?: number
	private timeSeconds = 0
	private lastTimeSeconds = 0

	private thunder = false
	private flash = 0
	private nextFlashAt = 0

	private elevationResolved = false
	private hiding = false

	/**
	 * @param geo City position as the owning node places it (geonames altitude)
	 * @param framingRadiusKm Extent the city frames at; sets every size here
	 * @param framingDistanceKm Camera distance the focus flight lands at
	 */
	constructor(context: EarthBrowserContext, geo: geographic, framingRadiusKm: number, framingDistanceKm: number) {
		super()
		this.context = context
		this.geo = geo
		this.framingRadiusKm = framingRadiusKm
		this.framingDistanceKm = framingDistanceKm

		this.plinth = new VignettePlinthNode(context)
		this.cloud = new VignetteCloudNode(context)
		this.precip = new VignettePrecipNode(context)
		this.addNode(this.plinth)
		this.addNode(this.cloud)
		this.addNode(this.precip)

		this.placeFrame()
		this.applyWeather()
		this.applyOpacity()
	}

	// ========== Lifecycle ==========

	public show(): void {
		this.hiding = false
		this.animateFade(1, cShowDuration, easing.cubicOut)
	}

	/** Fade out, then report so the owner can remove the node */
	public hide(onHidden: () => void): void {
		if (this.hiding) return
		this.hiding = true
		this.animateFade(0, cHideDuration, easing.quadraticOut, onHidden)
	}

	private animateFade(target: number, duration: number, ease: (k: number) => number, onComplete?: () => void): void {
		this.fadeAnim?.stop()
		this.fadeAnim = new animation()
			.from(this.fade)
			.to(target)
			.duration(duration * Math.abs(target - this.fade))
			.easing(ease)
			.onUpdate(value => {
				this.fade = value
				this.applyOpacity()
				requestRender()
			})
			.onComplete(() => onComplete?.())
		this.fadeAnim.start()
	}

	public override destroy(): void {
		this.fadeAnim?.stop()
		super.destroy()
	}

	public snapshot(): VignetteSnapshot {
		return {
			visible: this.fade > 0,
			opacity: this.fade * this.distanceFade,
			framingRadiusKm: this.framingRadiusKm,
			weather: this.effectiveWeather(),
			sunElevation: this.sunElevation,
			flash: this.flash,
		}
	}

	/** Preview conditions (dev): partial fields merge over the live sample; null clears */
	public setWeatherOverride(weather: Partial<CurrentWeather> | null): void {
		this.weatherOverride = weather
		this.applyWeather()
		this.applyOpacity()
		requestRender()
	}

	private effectiveWeather(): CurrentWeather | null {
		if (!this.weatherOverride) return this.weather
		const base: CurrentWeather = this.weather ?? {
			temp_c: null, dewpoint_c: null, humidity_pct: null, pressure_hpa: null,
			pressure_trend_3h_hpa: null, wind_speed_mps: null, wind_dir_deg: null,
			wind_gust_mps: null, cloud_cover_pct: null, precip_rate_mmh: null,
			feels_like_c: null, weather_code: "unknown", weather_desc: "Unknown",
			source: "override", time: "",
		}
		return { ...base, ...this.weatherOverride }
	}

	// ========== Frame ==========

	/**
	 * Local frame at the city: +Y up, +Z north, +X west. The parent node
	 * supplies the translation to the city (at its geonames altitude); the
	 * frame lifts to the terrain height the globe actually renders.
	 */
	private placeFrame(): void {
		const xRot = matrix.axisRotation(vec3.unitX, this.geo.latitude - trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, this.geo.longitude + trig.pi)
		const rotation = yRot.premultiply(xRot)

		const surface = new geographic(this.geo.longitude, this.geo.latitude, 0)
		const displayKm = this.context.displayElevationKm(surface)
		// A hair above the mesh so the plinth doesn't z-fight the terrain
		const liftKm = displayKm - this.geo.altitude + this.framingRadiusKm * 0.004
		const up = normal(this.geo.cartesian)
		this.transform = matrix.translate(up.multiply(liftKm)).premultiply(rotation)
		this.elevationResolved = this.context.baseElevation.loaded
	}

	// ========== Weather ==========

	private sampleIfDue(): void {
		if (this.sampleInFlight) return
		const service = getWeatherGridService()
		// Never trigger the grid download from here; the locations layer
		// owns that decision (embeds may have weather off)
		if (!service.isCurrentGridLoaded) return

		const now = Date.now()
		const clockMs = this.context.clock.clockTime.date.getTime()
		const due = this.sampledAtMs === 0
			|| Math.abs(clockMs - this.sampledClockMs) >= cClockResampleMs
			|| now - this.sampledAtMs >= cRealtimeResampleMs
		if (!due) return

		this.sampleInFlight = true
		const latDeg = this.geo.latitude * trig.deg
		const lonDeg = this.geo.longitude * trig.deg
		service.getCurrentWeather(latDeg, lonDeg, clockMs / 1000).then(weather => {
			this.weather = weather
			this.sampledAtMs = Date.now()
			this.sampledClockMs = clockMs
			this.applyWeather()
			requestRender()
		}).catch(() => {
			this.sampledAtMs = Date.now()
			this.sampledClockMs = clockMs
		}).finally(() => {
			this.sampleInFlight = false
		})
	}

	/** Map the sampled conditions onto the three parts' styles and sizes */
	private applyWeather(): void {
		const w = this.effectiveWeather()
		const R = this.framingRadiusKm
		const plinthRadius = 0.5 * R

		this.plinth.transform = matrix.scale(plinthRadius)
		this.plinth.tint = temperatureTint(w?.temp_c ?? null)

		if (!w) {
			this.cloud.visible = false
			this.precip.visible = false
			this.plinth.coverage = 0
			this.thunder = false
			return
		}

		const code = w.weather_code
		const precipRate = Math.max(0, w.precip_rate_mmh ?? 0)
		const raining = precipRate > 0.05 && code !== "clear"
		const snowing = code === "snow" || code === "heavy_snow" || code === "sleet"
		this.thunder = code === "thunderstorm"

		// Cover: the sampled fraction, never thin under active precipitation
		let coverage = clamp((w.cloud_cover_pct ?? 0) / 100, 0, 1)
		if (raining) coverage = Math.max(coverage, 0.7)
		// Overcast flattens the puffs into a deck; heavy rain darkens it
		const stratus = clamp((coverage - 0.7) / 0.25, 0, 1)
		const darkness = clamp(Math.sqrt(precipRate / 6), 0, 1) * 0.85 + (code.startsWith("heavy") ? 0.15 : 0)

		// Wind in the local frame (+X west, +Z north): the blowing-to vector
		const speed = w.wind_speed_mps ?? 0
		const toRad = ((w.wind_dir_deg ?? 0) + 180) * trig.rad
		const windX = -Math.sin(toRad)
		const windZ = Math.cos(toRad)

		// Cloud ellipsoid: cumulus vs. deck, blended by stratus
		const cy = lerp(0.5, 0.42, stratus) * R
		const rx = lerp(0.34, 0.42, stratus) * R
		const ry = lerp(0.2, 0.13, stratus) * R
		this.cloud.visible = coverage >= 0.02
		this.cloud.transform = matrix.scale(rx, ry, rx).translateBy(0, cy, 0)
		const driftRate = Math.min(0.004 + speed * 0.0012, 0.03)
		this.cloud.setStyle({
			coverage,
			stratus,
			darkness: clamp(darkness, 0, 1),
			drift: new vec2(windX * driftRate, windZ * driftRate),
		})

		// Precipitation from just inside the cloud base to the plinth
		this.precip.visible = raining
		if (raining) {
			const cloudBase = cy - ry * 0.75
			// Horizontal drift per km of fall: wind over terminal velocity
			const slantScale = snowing ? Math.min(speed / 3, 1.2) : Math.min(speed / 8, 0.8)
			this.precip.setStyle({
				density: clamp(Math.sqrt(precipRate / 8), 0.06, 1),
				snow: snowing,
				topY: cloudBase + ry * 0.3,
				bottomY: 0.01 * R,
				radius: Math.min(plinthRadius, rx * 0.9),
				slant: new vec2(windX * slantScale, windZ * slantScale),
				size: (snowing ? 0.025 : 0.06) * R,
			})
		}

		this.plinth.coverage = coverage
	}

	// ========== Per frame ==========

	private applyOpacity(): void {
		const opacity = this.fade * this.distanceFade * (this.effectiveWeather() ? 1 : 0)
		this.plinth.opacity = opacity
		this.cloud.opacity = opacity
		this.precip.opacity = opacity
	}

	private updateFlash(dt: number): void {
		if (!this.thunder) {
			this.flash = 0
			this.cloud.flash = 0
			return
		}
		this.flash *= Math.exp(-dt / cFlashDecayS)
		if (this.timeSeconds >= this.nextFlashAt) {
			this.flash = 0.6 + Math.random() * 0.4
			// Occasional double strike: a short gap, otherwise a long one
			const gap = Math.random() < 0.25 ? 0.12 + Math.random() * 0.2 : cFlashMinGapS + Math.random() * (cFlashMaxGapS - cFlashMinGapS)
			this.nextFlashAt = this.timeSeconds + gap
		}
		this.cloud.flash = this.flash
	}

	public override render(state: RenderState): RenderActionSet[] {
		if (!this.visible || (this.fade <= 0 && !this.fadeAnim?.running)) {
			return []
		}

		// Continuous seconds since first draw for the drift and the fall
		this.startJulian ??= state.realTime.julian
		this.timeSeconds = (state.realTime.julian - this.startJulian) * 86400
		const dt = Math.min(Math.max(this.timeSeconds - this.lastTimeSeconds, 0), 0.1)
		this.lastTimeSeconds = this.timeSeconds
		this.cloud.timeSeconds = this.timeSeconds
		this.precip.timeSeconds = this.timeSeconds

		// Terrain may stream in after the vignette appears
		if (!this.elevationResolved && this.context.baseElevation.loaded) {
			this.placeFrame()
		}

		this.sampleIfDue()

		// The parent's model matrix is the city placement
		const cityPosition = state.modelMatrix.position
		const up = normal(cityPosition)
		this.sunElevation = dot(up, RenderState.lightDirection)
		this.cloud.sunElevation = this.sunElevation
		this.plinth.night = clamp((0.05 - this.sunElevation) / 0.15, 0, 1)

		// Dissolve when the camera closes in past plinth scale, and again
		// well before the focus auto-dismisses on zoom-out
		const distance = state.camera.position.subtract(cityPosition).length
		const R = this.framingRadiusKm
		const near = clamp((distance - cNearFadeEnd * R) / ((cNearFadeStart - cNearFadeEnd) * R), 0, 1)
		const D = this.framingDistanceKm
		const far = 1 - clamp((distance - cFarFadeStartDistances * D) / ((cFarFadeEndDistances - cFarFadeStartDistances) * D), 0, 1)
		this.distanceFade = near * far

		this.updateFlash(dt)
		this.applyOpacity()

		if (this.fade * this.distanceFade > 0) {
			// Rain and lightning animate every frame while on screen
			requestRender()
		}
		return super.render(state)
	}
}

function lerp(a: number, b: number, t: number): number {
	return a + (b - a) * t
}

/**
 * Plinth tint by temperature: icy blue through the location label blue to
 * a warm amber. Null (no reading) uses the neutral location color.
 */
function temperatureTint(tempC: number | null) {
	const stops: Array<[number, [number, number, number]]> = [
		[-15, [0.55, 0.75, 1.0]],
		[5, [0.7, 0.85, 1.0]],
		[20, [0.9, 0.93, 0.92]],
		[35, [1.0, 0.72, 0.42]],
	]
	if (tempC === null) {
		return rgba(0.7, 0.85, 1.0, 0.9)
	}
	const t = clamp(tempC, stops[0][0], stops[stops.length - 1][0])
	for (let i = 1; i < stops.length; i++) {
		const [t0, c0] = stops[i - 1]
		const [t1, c1] = stops[i]
		if (t <= t1) {
			const k = (t - t0) / (t1 - t0)
			return rgba(lerp(c0[0], c1[0], k), lerp(c0[1], c1[1], k), lerp(c0[2], c1[2], k), 0.9)
		}
	}
	const last = stops[stops.length - 1][1]
	return rgba(last[0], last[1], last[2], 0.9)
}
